import React from 'react';

import { DropdownMultiple } from 'src/app/components/lib/dropdown';
import { FormSection } from 'src/app/components/lib/form';
import { Radio, RadioGroup } from 'src/app/components/lib/radio';
import { WizardCard } from 'src/app/components/lib/wizard/card';
import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';

import { useDiscountWizardStateContext } from '../../state';
import { DetailsStepActions } from '../../state/details';

import { TextWrapper } from './styled';
import { useDetailsStepData } from './use-details-data';

export function AvailabilityCard() {
  const { locationOptions, locationsDataLoading, orderSourceOptions, onlinePlatformTypesLoading } =
    useDetailsStepData();
  const isOrderSourcesEnabled = useLDFlag(LD.DISCOUNT_BY_ORDER_TYPE_MENU_TYPE, false);

  const {
    detailsStepState: { availableLocIds, availableOnline, platformTypeRestrictions },
    detailsStepDispatch,
  } = useDiscountWizardStateContext();

  const orderSourceIds = platformTypeRestrictions.map((restriction) => restriction.PlatformTypeId);
  // Build a readable selection summary for order sources (show up to 3, then "and x more")
  const selectedOrderSources = orderSourceOptions.filter((o) => orderSourceIds.includes(Number(o.id)));
  const orderSourceSelectionText = React.useMemo(() => {
    const labels = selectedOrderSources.map((o) => o.label).filter(Boolean);
    if (labels.length === 0) {
      return '';
    }
    const maxToShow = 3;
    if (labels.length <= maxToShow) {
      return labels.join(', ');
    }
    const shown = labels.slice(0, maxToShow).join(', ');
    const remaining = labels.length - maxToShow;
    return `${shown} and ${remaining} more`;
  }, [selectedOrderSources]);

  return (
    <WizardCard description={<AvailabilityDescription />} title='Where will it be offered'>
      <FormSection>
        <DropdownMultiple
          disabled={locationsDataLoading}
          helpText='All locations'
          label='Location(s) available:'
          labelPlacement='top'
          options={locationOptions}
          setValue={(newSelections) =>
            detailsStepDispatch({
              type: DetailsStepActions.SET_AVAILABLE_LOC_IDS,
              payload: newSelections.map((selection) => Number(selection.id)),
            })
          }
          value={locationOptions.filter((loc) => availableLocIds.includes(Number(loc.id)))}
          withinModal
        />
        <RadioGroup
          label='Available online:'
          labelPlacement='top'
          variant='boolean'
          onChange={({ target }) =>
            detailsStepDispatch({
              type: DetailsStepActions.SET_AVAILABLE_ONLINE,
              payload: target.value !== 'false',
            })
          }
        >
          <Radio checked={availableOnline} label='Yes' value />
          <Radio checked={!availableOnline} label='No' value={false} />
        </RadioGroup>
        {isOrderSourcesEnabled && availableOnline && (
          <DropdownMultiple
            disabled={onlinePlatformTypesLoading}
            helpText='All order sources'
            label='Order sources:'
            labelPlacement='top'
            options={orderSourceOptions}
            selectionText={orderSourceSelectionText}
            setValue={(newSelections) =>
              detailsStepDispatch({
                type: DetailsStepActions.SET_ORDER_SOURCE_IDS,
                payload: newSelections.map((selection) => ({
                  PlatformTypeId: Number(selection.id),
                  IsExclusion: false,
                })),
              })
            }
            value={selectedOrderSources}
            withinModal
          />
        )}
      </FormSection>
    </WizardCard>
  );
}

function AvailabilityDescription() {
  return (
    <TextWrapper>
      <p>You can limit where the discount is offered by specifying available locations.</p>
      <p>Available online determines whether your discount will be available on your website.</p>
    </TextWrapper>
  );
}
